import { ChangeDetectionStrategy, Component, computed, inject, TrackByFunction } from '@angular/core';
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common';
import { CardShellComponent } from '../card-shell/card-shell.component';
import { ImageScaleBreakpointService } from '../../../image-scale-breakpoint.service';
import { environment } from '../../../environments/environment';
import { Channel, Group, VoiceClient } from '../../../model/teamspeak';
import { DataService } from '../data.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgxSkeletonLoaderConfigTheme, NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { catchError, map, startWith } from 'rxjs/operators';
import { Observable, of, pipe, UnaryFunction } from 'rxjs';

import { ANGULAR_BREAKPOINTS } from '../../../image-breakpoints';

type State<T> =
  | { data: null; error: null; status: 'loading' }
  | { data: T; error: null; status: 'loaded' }
  | { data: null; error: Error; status: 'error' };

function toState<T>(): UnaryFunction<Observable<T>, Observable<State<T>>> {
  return pipe(
    map(value => {
      return { status: 'loaded' as const, data: value, error: null };
    }),
    catchError((err: Error) => {
      return of({ status: 'error' as const, data: null, error: err });
    }),
    startWith({ status: 'loading' as const, error: null, data: null })
  );
}

@Component({
  selector: 'app-ts-viewer',
  imports: [NgxSkeletonLoaderModule, NgOptimizedImage, NgTemplateOutlet, CardShellComponent],
  templateUrl: './ts-viewer.component.html',
  styleUrl: './ts-viewer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TsViewerComponent {
  private readonly breakpointService = inject(ImageScaleBreakpointService);
  private readonly dataService = inject(DataService);
  protected cardTopImageSizes = this.breakpointService.generateBootstrapBreakpoints({
    xs: 'calc(100vw -24px)',
    sm: '32.25rem',
    md: '28.75rem',
    lg: '18.5rem',
    xl: '17rem',
    xxl: '20rem',
  });

  protected readonly state = toSignal(this.dataService.teamSpeakUpdates$.pipe(toState()), {
    requireSync: true,
  });

  protected readonly dataAvailable = computed(() => this.state().status === 'loaded');

  trackChannel: TrackByFunction<Channel> = (index, item) => {
    return item.id;
  };
  trackGroup: TrackByFunction<Group> = (index, item) => {
    return item.name;
  };

  clientIconBase(client: VoiceClient) {
    const conditionToImageMap = {
      'client-away': (c: VoiceClient) => !!c.away,
      'output-muted': (c: VoiceClient) => !!c.outputMuted,
      'input-muted': (c: VoiceClient) => !!c.inputMuted,
      'hardware-input-muted': (c: VoiceClient) => !!c.noInputHardware,
      'hardware-output-muted': (c: VoiceClient) => !!c.noOutputHardware,
      'client-channel-commander-talking': (c: VoiceClient) => !!c.channelCommander && !!c.talking,
      'client-channel-commander': (c: VoiceClient) => !!c.channelCommander,
      'client-talking': (c: VoiceClient) => !!c.talking,
      'client-normal': () => true,
    };

    return Object.entries(conditionToImageMap).find(([, fn]) => fn(client))?.[0] ?? 'client-normal';
  }

  clientIcon(client: VoiceClient) {
    return `${this.clientIconBase(client)}.webp`;
  }

  clientIconAvif(client: VoiceClient) {
    return `${this.clientIconBase(client)}.avif`;
  }

  channelIconBase(channel: Channel) {
    const conditionToImageMap = {
      'channel-full': (c: Channel) =>
        c.maxClients !== null && c.maxClients !== undefined && c.maxClients <= (c.clients?.length ?? 0),
      'channel-password': (c: Channel) => !!c.isPasswordProtected,
      'channel-normal': () => true,
    };

    return Object.entries(conditionToImageMap).find(([, fn]) => fn(channel))?.[0] ?? 'channel-normal';
  }

  channelIcon(channel: Channel) {
    return `${this.channelIconBase(channel)}.webp`;
  }

  channelIconAvif(channel: Channel) {
    return `${this.channelIconBase(channel)}.avif`;
  }

  getIconUrl(iconId: number) {
    if ([100, 200, 300, 500, 600].includes(iconId)) {
      return `assets/img/ts/defaultGroups/${iconId}.webp`;
    }
    return `${environment.endpoints.api}/teamspeak/icon/${iconId}`;
  }

  getIconAvifUrl(iconId: number): string | null {
    if ([100, 200, 300, 500, 600].includes(iconId)) {
      return `assets/img/ts/defaultGroups/${iconId}.avif`;
    }
    return null;
  }

  asClient = (something: unknown) => something as VoiceClient;
  asChannel = (something: unknown) => something as Channel;

  private common: NgxSkeletonLoaderConfigTheme = {
    height: '14px',
    'margin-bottom': 0,
  };

  channel: NgxSkeletonLoaderConfigTheme = {
    ...this.common,
    width: '14px',
  };

  user: NgxSkeletonLoaderConfigTheme = {
    ...this.common,
    width: '14px',
    'border-radius': '9px',
  };

  text(number: number): NgxSkeletonLoaderConfigTheme {
    return {
      ...this.common,
      width: `${number}rem`,
    };
  }

  getFlagIconUrl(country: string | undefined) {
    return `assets/img/ts/flags/${country?.toLowerCase()}.webp`;
  }

  getFlagIconAvifUrl(country: string | undefined) {
    return `assets/img/ts/flags/${country?.toLowerCase()}.avif`;
  }

  protected readonly Error = Error;

  isHttpStatus(error: Error, status: number): boolean {
    return 'status' in error && error.status === status;
  }

  protected readonly avifSrcset = ANGULAR_BREAKPOINTS.map(w => `/assets/img/games/teamspeak.${w}px.avif ${w}w`).join(
    ', '
  );
}
